'use client';

import { useProfileQuery } from '@/lib/services/profile/hooks';
import { useLawFirmProfile } from '@/lib/services/account/hooks';
import { LawFirmUser } from '@/lib/services/account/types';
import { LawFirm } from '@/lib/api/account-api';
import type { ProfileResponse } from '@/lib/services/profile';
import type { LawFirmProfile } from '@/lib/services/account/types';

/**
 * Transform profile response data into LawFirmUser format
 * Expected by existing card components
 */
export function transformToLawFirmUser(
  profileData: ProfileResponse
): LawFirmUser {
  const { user, permissions } = profileData;
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    middleName: user.middleName || '',
    lastName: user.lastName,
    /*
      Both are real `/profile` fields and both must be carried across, or the
      surfaces built on this transform read them as permanently unset.

      This is a hand-written projection, so a field the API returns but this map
      omits is simply absent downstream — with no error and nothing to notice.
      The profile card's Phone row saved correctly and then re-rendered as
      "Set phone…" for exactly that reason: the PUT landed, the refetch landed,
      and the value was dropped on the way to the component.
    */
    phoneNumber: user.phoneNumber ?? null,
    title: user.title ?? null,
    // The profile page link (VALE-2029). Dropped here, the row would save and
    // re-render blank, the failure the warning above describes.
    profileUrl: user.profileUrl ?? null,
    // VALE-1227, and the FOURTH field the warning above is about. The Pronouns
    // row is the profile's half of a list shared with the team drawer, and the
    // team drawer reads a different endpoint — so omitting it here would leave
    // one of the two surfaces reading the field as permanently unset, with the
    // write, the API and the other surface all working perfectly.
    pronouns: user.pronouns ?? null,
    // VALE-540, and the second field to be caught by the warning above: the
    // Office row saved correctly and re-rendered blank because this projection
    // dropped the value on the way to the component.
    officeId: user.officeId ?? null,
    role: user.role || 'user',
    status: 'active',
    preference: user.preferences,
    handwrittenSignatureFile: user.handwrittenSignatureFile,
    emailSignature: user.emailSignature || '',
    createdAt: user.createdAt || new Date().toISOString(),
    updatedAt: user.updatedAt || new Date().toISOString(),
    invitedAt: undefined,
    lawFirm: undefined,
    isAdministrator:
      (user as { isAdministrator?: boolean }).isAdministrator ||
      permissions.includes('account:manage'),
    hasSignatureAccess: true,
    totpEnabledAt: user.totpEnabledAt || null
  };
}

/**
 * Transform law firm profile data into LawFirm format
 * Expected by existing card components
 *
 * **Every field the API returns must be carried across explicitly**, and the roll-call of
 * what happens otherwise is now seven long: `primaryContactUser`, `nextBillingDate`, the
 * profile Phone row, VALE-540's Office row, VALE-777's `defaultOfficeId`, and — added
 * here — `fax` and `hasBillingAccount` (VALE-1301). Each behaved identically: the write
 * landed, the refetch landed, and this hand-written projection dropped the value on the
 * way to the component, so the surface re-rendered as though nothing had been saved and
 * nothing anywhere reported it.
 *
 * Six one-off tests were written for the first five. The seventh field broke anyway, so
 * the guard is now a set assertion rather than another instance:
 * `components/account/__tests__/transform-to-law-firm.test.ts` builds a
 * `Required<LawFirmProfile>` fixture — which the type checker will not let you leave
 * incomplete — and asserts every key on it arrives. A field added to `LawFirmProfile` and
 * missed here fails there, by construction.
 */
export function transformToLawFirm(profile: LawFirmProfile): LawFirm {
  return {
    id: profile.id,
    name: profile.name,
    email: profile.email || '',
    phoneNumber: profile.phoneNumber || '',
    /*
      VALE-1301. The Fax row saved correctly the whole time — `fax` was on the request
      schema and the backend wrote it — and then re-rendered empty because this line was
      missing, which reads to a firm exactly like a save that did not happen.
    */
    fax: profile.fax ?? null,
    valeEmail: profile.email || '',
    trustAccountNeedInfo: '',
    verifiedAt: profile.verifiedAt,
    postalAddress: profile.postalAddress
      ? {
          poBox: profile.postalAddress.poBox || '',
          streetAddress: profile.postalAddress.streetAddress || '',
          suburb: profile.postalAddress.suburb || '',
          state: profile.postalAddress.state || '',
          postcode: profile.postalAddress.postcode || '',
          country: profile.postalAddress.country || ''
        }
      : undefined,
    businessAddress: profile.businessAddress
      ? {
          streetAddress: profile.businessAddress.streetAddress || '',
          suburb: profile.businessAddress.suburb || '',
          state: profile.businessAddress.state || '',
          postcode: profile.businessAddress.postcode || '',
          country: profile.businessAddress.country || ''
        }
      : undefined,
    trustBankAccount: profile.trustBankAccount,
    officeBankAccount: profile.officeBankAccount,
    apcaUserId: profile.apcaUserId,
    primaryContact: profile.primaryContact,
    primaryContactUser: profile.primaryContactUser,
    correspondencePreferences: profile.correspondencePreferences,
    // VALE-777, and the THIRD field caught by the warning on
    // `transformToLawFirmUser` above — after the profile Phone row and
    // VALE-540's Office row. The nomination saved correctly, the API returned
    // it, and this projection dropped it on the way to the component, so the
    // picker read "Automatic" no matter what the firm had chosen.
    defaultOfficeId: profile.defaultOfficeId ?? null,
    letterheadFilename: profile.letterheadFilename,
    letterHeadFile: (
      profile as LawFirmProfile & { letterHeadFile?: LawFirm['letterHeadFile'] }
    ).letterHeadFile,
    htmlEmailSignature: (
      profile as LawFirmProfile & { htmlEmailSignature?: string }
    ).htmlEmailSignature,
    membershipStatus: profile.membershipStatus,
    membershipTier: profile.membershipTier,
    trialStartedAt: profile.trialStartedAt,
    trialExpiresAt: profile.trialExpiresAt,
    membershipActivatedAt: profile.membershipActivatedAt,
    nextBillingDate: profile.nextBillingDate,
    scheduledCancellationDate: profile.scheduledCancellationDate,
    // Invoice billing details (LF-3774 follow-ups)
    billingName: profile.billingName,
    billingEmail: profile.billingEmail,
    billingAbn: profile.billingAbn,
    billingAddress: profile.billingAddress
      ? {
          streetAddress: profile.billingAddress.streetAddress || '',
          suburb: profile.billingAddress.suburb || '',
          state: profile.billingAddress.state || '',
          postcode: profile.billingAddress.postcode || '',
          country: profile.billingAddress.country || ''
        }
      : null,
    /*
      VALE-1301, and the same drop as `fax` above. The Billing tab gates its
      payment-methods section on this (VALE-428), so leaving it unmapped made
      `showPaymentMethods` permanently false: the API answered `true`, the projection
      dropped it, and a firm with a Stripe customer saw no payment methods at all.
    */
    hasBillingAccount: profile.hasBillingAccount,
    configuration: profile.configuration,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt
  };
}

/**
 * Hook providing transformed user data for account tab pages.
 * Only fetches user profile — used by tabs that only need user info.
 */
export function useAccountUser() {
  const { data: profileData, isLoading, error } = useProfileQuery();

  if (!profileData || isLoading) {
    return { user: null, permissions: [] as string[], isLoading, error };
  }

  return {
    user: transformToLawFirmUser(profileData),
    permissions: profileData.permissions,
    isLoading,
    error
  };
}

/**
 * Hook providing transformed law firm data for account tab pages.
 * Only fetches law firm profile — used by tabs that need firm info.
 */
export function useAccountLawFirm() {
  const { profile, isLoading, error } = useLawFirmProfile();

  if (!profile || isLoading) {
    return { lawFirm: null, isLoading, error };
  }

  return {
    lawFirm: transformToLawFirm(profile),
    isLoading,
    error
  };
}
